import React, { useState, useMemo, useRef, useEffect } from "react";
import { Flex, Text, Card, Table, Badge, Select, TextField, Button, Box, Tooltip, SegmentedControl,
  Progress } from "@radix-ui/themes";
import { MagnifyingGlassIcon, ReloadIcon, ChevronLeftIcon, ChevronRightIcon, EnvelopeClosedIcon, ChatBubbleIcon, 
  MobileIcon } from "@radix-ui/react-icons";
import { useProfileContext, useLocationContext } from "context";
import { useEmailActivityLogs } from "hooks";
import { useToast } from '../../context/ToastContext';
import { ToastAlert } from "components";

// Event badge colors
const EVENT_COLORS = {
  queued: "amber",
  sent: "blue",
  processed: "blue",
  delivered: "green",
  open: "cyan",
  read: "cyan",
  click: "purple",
  bounce: "red",
  failed: "red",
  undelivered: "orange",
  canceled: "gray",
  spam_report: "orange",
  subscribe: "green",
  unsubscribe: "gray",
  received: "purple",
  auto_reply_sent: "blue",
};

const CHANNEL_COLORS = {
  email: "blue",
  sms: "cyan",
  whatsapp: "green",
};

// Event display labels
const EVENT_LABELS = {
  queued: "Queued",
  sent: "Sent",
  processed: "Processed",
  delivered: "Delivered",
  open: "Opened",
  read: "Read",
  click: "Clicked",
  bounce: "Bounced",
  failed: "Failed",
  undelivered: "Undelivered",
  canceled: "Canceled",
  spam_report: "Spam",
  subscribe: "Resubscribed",
  unsubscribe: "Unsubscribed",
  received: "Received",
  auto_reply_sent: "Auto reply sent",
};

const CHANNEL_LABELS = {
  email: "Email",
  sms: "SMS",
  whatsapp: "WhatsApp",
};

// Format timestamp to readable date
const formatTimestamp = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const TruncatedText = ({ children, size, weight, color }) => {
  const text = typeof children === 'string' ? children : String(children);
  const [isTruncated, setIsTruncated] = useState(false);
  const containerRef = useRef(null);
  
  useEffect(() => {
    const checkTruncation = () => {
      const container = containerRef.current;
      if (container) {
        setIsTruncated(container.scrollWidth > container.clientWidth);
      }
    };
    
    // Initial check
    checkTruncation();
    
    // Recheck on window resize
    window.addEventListener('resize', checkTruncation);
    
    return () => window.removeEventListener('resize', checkTruncation);
  }, [text]);
  
  const textElement = (
    <Box as="div" 
      ref={containerRef} 
      style={{
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }}
    >
      <Text 
        size={size} 
        weight={weight}
        color={color}
      >
        {text}
      </Text>
    </Box>
  );
  
  return isTruncated ? (
    <Tooltip content={text}>
      {textElement}
    </Tooltip>
  ) : textElement;
};

export default function MarketingActivityLogs() {
  const { userProfile } = useProfileContext();
  const { locationsData } = useLocationContext();
  const { setToastMessage } = useToast();
  
  // Channel segment state
  const [selectedSegment, setSelectedSegment] = useState("all");
  
  // Filters state
  const [filters, setFilters] = useState({
    event: "all",
    is_test: "all",
    location_id: "all",
    recipient_email: "",
    page: 1,
    page_size: 25,
  });
  
  // Debounced search
  const [searchInput, setSearchInput] = useState("");

  const activityFilters = useMemo(() => ({
    ...filters,
    recipient_email: searchInput,
    channel: selectedSegment,
  }), [filters, searchInput, selectedSegment]);
  
  // Fetch data
  const { logs, pagination, isLoading: logsLoading, refetch: refetchLogs } = useEmailActivityLogs(
    userProfile?.organisation_id, 
    activityFilters
  );

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  // Handle search with debounce effect
  const handleSearch = (value) => {
    setSearchInput(value);
    setFilters(prev => ({ ...prev, page: 1 }));
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  // Refresh data
  const handleRefresh = () => {
    refetchLogs();
    setToastMessage('Activity refreshed');
  };

  // Location options for filter
  const locationOptions = useMemo(() => {
    if (!locationsData) return [];
    return locationsData.map(loc => ({ id: loc.id, name: loc.name }));
  }, [locationsData]);

  const searchPlaceholder = selectedSegment === "sms" || selectedSegment === "whatsapp"
    ? "Search by phone..."
    : selectedSegment === "all"
      ? "Search by recipient..."
      : "Search by email...";

  const renderEventBadge = (log) => {
    const badge = (
      <Badge color={EVENT_COLORS[log.event] || "gray"}>
        {EVENT_LABELS[log.event] || log.event}
      </Badge>
    );

    if (log.event === 'queued') {
      return (
        <Tooltip 
          content={
            log.is_pending 
              ? `Scheduled to send at: ${formatTimestamp(log.scheduled_send_at)}`
              : "Queued for delivery"
          }
        >
          {badge}
        </Tooltip>
      );
    }

    return badge;
  };

  const renderChannelBadge = (channel) => {
    if (channel === "sms") {
      return (
        <Badge color={CHANNEL_COLORS[channel] || "gray"}>
          <Flex gap="1" align="center">
            <MobileIcon />
            <Text>{CHANNEL_LABELS[channel] || channel}</Text>
          </Flex>
        </Badge>
      );
    }

    if (channel === "whatsapp") {
      return (
        <Badge color={CHANNEL_COLORS[channel] || "gray"}>
          <Flex gap="1" align="center">
            <ChatBubbleIcon />
            <Text>{CHANNEL_LABELS[channel] || channel}</Text>
          </Flex>
        </Badge>
      );
    }

    return (
      <Badge color={CHANNEL_COLORS[channel] || "gray"}>
        <Flex gap="1" align="center">
          <EnvelopeClosedIcon />
          <Text>{CHANNEL_LABELS[channel] || channel}</Text>
        </Flex>
      </Badge>
    );
  };

  return (
    <>
      {/* Loading Progress Bar */}
      {logsLoading && (
        <div style={{ 
          position: "fixed", 
          top: 0, 
          left: 0, 
          width: "100%", 
          zIndex: 1000 
        }}>
          <Progress size="1" duration="1" />
        </div>
      )}
      
      <Flex direction="column" gap="5" style={{ alignSelf: "stretch", alignItems: "flex-start", flex: "1 0 0", width: "100%" }}>
        {/* Channel Segmented Control */}
        <Flex justify="between" align="center" style={{ alignSelf: "stretch" }}>
          <SegmentedControl.Root value={selectedSegment} onValueChange={setSelectedSegment}>
            <SegmentedControl.Item value="all">All</SegmentedControl.Item>
            <SegmentedControl.Item value="email">
              <Flex gap="1" align="center">
                <EnvelopeClosedIcon />
                <Text>Email</Text>
              </Flex>
            </SegmentedControl.Item>
            <SegmentedControl.Item value="sms">
              <Flex gap="1" align="center">
                <MobileIcon />
                <Text>SMS</Text>
              </Flex>
            </SegmentedControl.Item>
            <SegmentedControl.Item value="whatsapp">
              <Flex gap="1" align="center">
                <ChatBubbleIcon />
                <Text>WhatsApp</Text>
              </Flex>
            </SegmentedControl.Item>
          </SegmentedControl.Root>
        </Flex>

        {selectedSegment === "sms" ? (
          <Flex direction="column" style={{justifyContent: 'center', alignItems: 'center', alignSelf: 'stretch',
            border: '1px dashed var(--gray-a6)', borderRadius: 'var(--radius-4)', height: '100%', flex: '1 0 0'}}>
            <Text color="gray">SMS features coming soon.</Text>
          </Flex>
        ) : (
        <>
        {/* Filters Row */}
        <Flex gap="3" align="center" wrap="wrap" justify="between" style={{ alignSelf: "stretch" }}>
            <Flex gap="3" align="center" wrap="wrap" style={{ alignSelf: "stretch" }}>
            {/* Search by email */}
            <Box style={{ flex: 1, minWidth: "200px", maxWidth: "300px" }}>
              <TextField.Root 
                placeholder={searchPlaceholder}
                value={searchInput}
                onChange={(e) => handleSearch(e.target.value)}
              >
                <TextField.Slot>
                  <MagnifyingGlassIcon height="16" width="16" />
                </TextField.Slot>
              </TextField.Root>
            </Box>

            {/* Event filter */}
            <Select.Root value={filters.event} onValueChange={(value) => handleFilterChange("event", value)}>
              <Select.Trigger placeholder="All Events" style={{ minWidth: "140px" }} />
              <Select.Content>
                <Select.Item value="all">All Events</Select.Item>
                <Select.Item value="queued">Queued</Select.Item>
                <Select.Item value="sent">Sent</Select.Item>
                <Select.Item value="processed">Processed</Select.Item>
                <Select.Item value="delivered">Delivered</Select.Item>
                <Select.Item value="open">Opened</Select.Item>
                <Select.Item value="read">Read</Select.Item>
                <Select.Item value="click">Clicked</Select.Item>
                <Select.Item value="bounce">Bounced</Select.Item>
                <Select.Item value="failed">Failed</Select.Item>
                <Select.Item value="undelivered">Undelivered</Select.Item>
                <Select.Item value="canceled">Canceled</Select.Item>
                <Select.Item value="spam_report">Spam Reported</Select.Item>
                <Select.Item value="subscribe">Resubscribed</Select.Item>
                <Select.Item value="unsubscribe">Unsubscribed</Select.Item>
                <Select.Item value="received">Received</Select.Item>
                <Select.Item value="auto_reply_sent">Auto reply sent</Select.Item>
              </Select.Content>
            </Select.Root>

            {/* Live/test activity filter */}
            <Select.Root value={filters.is_test} onValueChange={(value) => handleFilterChange("is_test", value)}>
              <Select.Trigger aria-label="Activity type" style={{ minWidth: "150px" }} />
              <Select.Content>
                <Select.Item value="all">Live + Test</Select.Item>
                <Select.Item value="false">Live only</Select.Item>
                <Select.Item value="true">Test only</Select.Item>
              </Select.Content>
            </Select.Root>

            {/* Location filter */}
            <Select.Root value={filters.location_id} onValueChange={(value) => handleFilterChange("location_id", value)}>
              <Select.Trigger placeholder="All Locations" style={{ minWidth: "160px" }} />
              <Select.Content>
                <Select.Item value="all">All Locations</Select.Item>
                {locationOptions.map(loc => (
                  <Select.Item key={loc.id} value={String(loc.id)}>{loc.name}</Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Flex>
          {/* Refresh button */}
          <Tooltip content="Refresh activity logs">
            <Button variant="soft" color="gray" onClick={handleRefresh}>
              <ReloadIcon />
            </Button>
          </Tooltip>
        </Flex>

        {/* Activity Table */}
        <Card style={{ display: "flex", alignSelf: "stretch", flexDirection: "column", alignItems: "flex-start", 
          flex: "1 0 0", height: "100%", maxWidth: "100%", overflow: 'auto'}}>
          { logs.length === 0 ? (
            <Flex justify="center" align="center" style={{ width: "100%", height: "100%" }}>
              <Text color="gray">No activity to show</Text>
            </Flex>
          ) : (
            <Table.Root layout="fixed" style={{ width: "100%", height: "100%"}}>
              <Table.Header style={{ position: "sticky", top: "0", zIndex: "1", backgroundColor: "var(--color-panel-solid)" }}>
                <Table.Row align="center">
                  <Table.ColumnHeaderCell>
                    <TruncatedText size="2" weight="medium">Recipient</TruncatedText>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>
                    <TruncatedText size="2" weight="medium">Sender</TruncatedText>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>
                    <TruncatedText size="2" weight="medium">Channel</TruncatedText>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>
                    <TruncatedText size="2" weight="medium">Details</TruncatedText>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>
                    <TruncatedText size="2" weight="medium">Event</TruncatedText>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>
                    <TruncatedText size="2" weight="medium">Location</TruncatedText>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>
                    <TruncatedText size="2" weight="medium">Template</TruncatedText>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>
                    <TruncatedText size="2" weight="medium">Timestamp</TruncatedText>
                  </Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {logs.map((log) => (
                  <Table.Row key={log.id}>
                    <Table.Cell>
                      <TruncatedText weight="regular" size="2" color="gray">{log.recipient || "—"}</TruncatedText>
                    </Table.Cell>
                    <Table.Cell>
                      <TruncatedText weight="regular" size="2" color="gray">{log.sender || log.sender_email || "—"}</TruncatedText>
                    </Table.Cell>
                    <Table.Cell>
                      {renderChannelBadge(log.channel)}
                    </Table.Cell>
                    <Table.Cell>
                      <TruncatedText weight="regular" size="2" color="gray">{log.details || "—"}</TruncatedText>
                    </Table.Cell>
                    <Table.Cell>
                      <Flex gap="1" align="center" wrap="wrap">
                        {renderEventBadge(log)}
                        {log.is_test && (
                          <Badge color="amber" variant="solid">Test</Badge>
                        )}
                      </Flex>
                    </Table.Cell>
                    <Table.Cell>
                      <TruncatedText weight="regular" size="2" color="gray">{log.location?.name || "—"}</TruncatedText>
                    </Table.Cell>
                    <Table.Cell>
                      <TruncatedText weight="regular" size="2" color="gray">{log.template?.name || "—"}</TruncatedText>
                    </Table.Cell>
                    <Table.Cell>
                      <TruncatedText weight="regular" size="2" color="gray">
                        {formatTimestamp(log.event_timestamp)}
                      </TruncatedText>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          )}
        </Card>

        {/* Pagination */}
        {pagination.numPages > 1 && (
          <Flex justify="between" align="center" style={{ alignSelf: "stretch" }}>
            <Text size="2" color="gray">
              Showing {((pagination.currentPage - 1) * filters.page_size) + 1} - {Math.min(pagination.currentPage * filters.page_size, pagination.count)} of {pagination.count} rows
            </Text>
            <Flex gap="2" align="center">
              <Button 
                variant="soft" 
                disabled={!pagination.hasPrevious}
                onClick={() => handlePageChange(pagination.currentPage - 1)}
              >
                <ChevronLeftIcon />
                Previous
              </Button>
              <Text size="2">Page {pagination.currentPage} of {pagination.numPages}</Text>
              <Button 
                variant="soft" 
                disabled={!pagination.hasNext}
                onClick={() => handlePageChange(pagination.currentPage + 1)}
              >
                Next
                <ChevronRightIcon />
              </Button>
            </Flex>
          </Flex>
        )}
        </>
        )}
      </Flex>
      {/* Toast Alert */}
      <ToastAlert />
    </>
  );
}
